import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import config from '../config';
import { useAuth } from '../App';
import './Landing.css';

// ==================== AUTO-LOGIN NUI (FiveM) ====================
function NuiAutoLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('waiting'); // waiting | loading | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // Signaler au parent (tablet.html) que React est prêt
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'nui_ready' }, '*');
    }

    // Fallback : token passé dans le hash #nui_token=xxx (si postMessage manqué)
    const hash = window.location.hash;
    if (hash.startsWith('#nui_token=')) {
      const token = decodeURIComponent(hash.slice('#nui_token='.length));
      if (token) {
        setStatus('loading');
        // Nettoyer le hash
        window.history.replaceState(null, '', '/');
        login(token);
        navigate('/dashboard');
        return;
      }
    }

    const handler = (event) => {
      const data = event.data;
      // Accepte depuis '*' car l'origine NUI FiveM est 'null'
      if (!data || data.type !== 'nui_auth') return;
      if (!data.token) {
        // Mapper les codes d'erreur génériques vers des messages lisibles
        const raw = data.error || '';
        let msg = raw;
        if (!raw || raw.includes('403') || raw.includes('Erreur')) {
          msg = 'Aucun compte bancaire trouvé. Rendez-vous en agence Fleeca pour ouvrir un compte.';
        }
        setErrorMsg(msg);
        setStatus('error');
        return;
      }
      setStatus('loading');
      login(data.token);
      navigate('/dashboard');
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [login, navigate]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--surface)',
    }}>
      <img src="/logo.png" alt="Fleeca Bank"
        style={{ width: 72, marginBottom: 24, borderRadius: 16 }}
        onError={e => { e.target.style.display = 'none'; }} />
      {status === 'waiting' && (
        <>
          <i className="fa-solid fa-building-columns spinner"
            style={{ fontSize: 36, color: 'var(--primary)', marginBottom: 16 }} />
          <p style={{ color: 'var(--on-surface)', fontFamily: 'Manrope', fontWeight: 700, fontSize: 18 }}>
            Fleeca Bank
          </p>
          <p style={{ color: 'var(--on-surface-variant)', fontFamily: 'Manrope', fontSize: 13, marginTop: 8 }}>
            Connexion en cours...
          </p>
        </>
      )}
      {status === 'loading' && (
        <>
          <i className="fa-solid fa-spinner spinner"
            style={{ fontSize: 36, color: 'var(--primary)', marginBottom: 16 }} />
          <p style={{ color: 'var(--on-surface-variant)', fontFamily: 'Manrope', fontSize: 13 }}>
            Chargement de votre compte...
          </p>
        </>
      )}
      {status === 'error' && (
        <>
          <i className="fa-solid fa-circle-exclamation"
            style={{ fontSize: 36, color: '#ef4444', marginBottom: 16 }} />
          <p style={{ color: '#ef4444', fontFamily: 'Manrope', fontWeight: 600, fontSize: 15 }}>
            Accès refusé
          </p>
          <p style={{ color: 'var(--on-surface-variant)', fontSize: 13, marginTop: 8, textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
            {errorMsg || 'Veuillez fermer et rouvrir l\'interface.'}
          </p>
        </>
      )}
    </div>
  );
}

// Détection FiveM CEF
const isIngame = () =>
  navigator.userAgent.includes('CFX') ||
  navigator.userAgent.includes('FiveM') ||
  window.invokeNative !== undefined;

// Navbar
function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  return (
    <nav className="landing-nav">
      <div className="nav-inner">
        <div className="nav-left">
          <div className="nav-logo">
            <img src="/logo.png" alt="Fleeca Bank" className="nav-logo-img" onError={e => { e.target.style.display='none'; }} />
            <span className="nav-logo-text">Fleeca Bank</span>
          </div>
          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#services" className="nav-link">Services</a>
            <a href="#securite" className="nav-link">Securite</a>
            <a href="#acces" className="nav-link">Acces</a>
          </div>
        </div>
        <div className="nav-actions">
          {user?.isAdmin && (
            <Link to="/admin" className="btn-admin-nav">
              <i className="fa-solid fa-shield-halved" />
              Panel Admin
            </Link>
          )}
          <a href={config.DISCORD_LOGIN_URL} className="btn-primary nav-cta">
            <i className="fa-brands fa-discord" />
            Connexion Discord
          </a>
          <button className="nav-burger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <i className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'}`} />
          </button>
        </div>
      </div>
    </nav>
  );
}

// Hero
function HeroSection() {
  return (
    <section className="hero-section">
      <div className="hero-bg">
        <div className="hero-overlay" />
      </div>
      <div className="hero-content">
        <span className="badge badge-primary">
          <i className="fa-solid fa-shield-halved" />
          Institution de confiance
        </span>
        <h1 className="hero-title">
          Vos fonds, notre priorite a <span className="text-green">Los Santos</span>
        </h1>
        <p className="hero-subtitle">
          L'excellence bancaire au coeur de la ville. Securite, discretion et croissance pour vos actifs les plus precieux.
        </p>
        <div className="hero-actions">
          <a href={config.DISCORD_LOGIN_URL} className="btn-primary hero-btn-main">
            <i className="fa-brands fa-discord" />
            Acceder a mon compte
          </a>
          <a href="#services" className="btn-secondary hero-btn-sec">
            Nos services
          </a>
        </div>
        {/* Stats */}
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-value">100%</span>
            <span className="hero-stat-label">Securise</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="hero-stat-value">24/7</span>
            <span className="hero-stat-label">Disponible</span>
          </div>
          <div className="hero-stat-divider" />
          <div className="hero-stat">
            <span className="hero-stat-value">RP</span>
            <span className="hero-stat-label">Nexa RP</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// Services Bento Grid
const SERVICES = [
  {
    icon: 'fa-wallet',
    title: 'Comptes Courants',
    desc: 'Gerez vos revenus quotidiens avec une fluidite inegalee. Acces instantane a vos fonds via nos distributeurs strategiquement places.',
    wide: true,
    color: 'primary',
  },
  {
    icon: 'fa-car',
    title: 'Prets & Credits',
    desc: 'Financement sur mesure pour vos acquisitions immobilieres a Rockford Hills ou vos vehicules de luxe.',
    wide: false,
    color: 'secondary',
    bullets: ['Taux preferentiels', 'Approbation en 24h'],
  },
  {
    icon: 'fa-right-left',
    title: 'Virements Instantanes',
    desc: 'Transferez vos fonds entre personnages ou vers des tiers en quelques secondes, directement depuis l\'interface.',
    wide: false,
    color: 'primary',
  },
  {
    icon: 'fa-clock-rotate-left',
    title: 'Historique Complet',
    desc: 'Consultez l\'integralite de vos transactions bancaires avec details et traçabilite complete.',
    wide: false,
    color: 'secondary',
  },
];

function ServicesSection() {
  return (
    <section className="services-section" id="services">
      <div className="section-inner">
        <div className="section-header">
          <div>
            <h2 className="section-title">Services Exclusifs</h2>
            <p className="section-subtitle">Une gamme complete de solutions financieres concues pour l'elite de San Andreas.</p>
          </div>
          <span className="section-watermark">FLEECA</span>
        </div>
        <div className="bento-grid">
          {SERVICES.map((s, i) => (
            <div key={i} className={`bento-card ${s.wide ? 'bento-wide' : ''}`}>
              <div className={`bento-icon bento-icon-${s.color}`}>
                <i className={`fa-solid ${s.icon}`} />
              </div>
              <h3 className="bento-title">{s.title}</h3>
              <p className="bento-desc">{s.desc}</p>
              {s.bullets && (
                <ul className="bento-bullets">
                  {s.bullets.map((b, j) => (
                    <li key={j}>
                      <i className="fa-solid fa-circle-check" style={{ color: 'var(--primary)' }} />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Securite
function SecuritySection() {
  return (
    <section className="security-section" id="securite">
      <div className="section-inner">
        <div className="security-card">
          <div className="security-left">
            <span className="badge badge-primary">
              <i className="fa-solid fa-lock" />
              Protection Active
            </span>
            <h2 className="security-title">Securite Maximale</h2>
            <p className="security-desc">
              Vos coffres sont proteges par les protocoles de securite les plus avances. Chiffrement de grade militaire pour toutes vos transactions numeriques.
            </p>
            <ul className="security-list">
              <li><i className="fa-solid fa-circle-check" /> Authentification Discord obligatoire</li>
              <li><i className="fa-solid fa-circle-check" /> Verification d'identite en 3 etapes</li>
              <li><i className="fa-solid fa-circle-check" /> Sessions chiffrees JWT</li>
              <li><i className="fa-solid fa-circle-check" /> Logs de toutes les transactions</li>
            </ul>
          </div>
          <div className="security-right">
            <div className="security-stats">
              <div className="security-stat">
                <span className="security-stat-value">100%</span>
                <span className="security-stat-label">Garanti</span>
              </div>
              <div className="security-stat-divider" />
              <div className="security-stat">
                <span className="security-stat-value">24/7</span>
                <span className="security-stat-label">Monitoring</span>
              </div>
              <div className="security-stat-divider" />
              <div className="security-stat">
                <span className="security-stat-value">0</span>
                <span className="security-stat-label">Failles</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// CTA / Acces
function AccessSection() {
  return (
    <section className="access-section" id="acces">
      <div className="section-inner">
        <div className="access-card">
          <div className="access-icon-wrap">
            <i className="fa-solid fa-building-columns access-icon" />
          </div>
          <h2 className="access-title">Accedez a votre compte</h2>
          <p className="access-desc">
            Connectez-vous avec votre compte Discord du serveur Nexa RP pour acceder a votre espace bancaire en ligne.
          </p>
          <div className="access-steps">
            <div className="access-step">
              <div className="step-num">1</div>
              <div>
                <strong>Connexion Discord</strong>
                <p>Autorisez l'application avec votre compte Discord</p>
              </div>
            </div>
            <i className="fa-solid fa-chevron-right step-arrow" />
            <div className="access-step">
              <div className="step-num">2</div>
              <div>
                <strong>Verification d'identite</strong>
                <p>Votre license FiveM est recuperee automatiquement</p>
              </div>
            </div>
            <i className="fa-solid fa-chevron-right step-arrow" />
            <div className="access-step">
              <div className="step-num">3</div>
              <div>
                <strong>Selection du personnage</strong>
                <p>Choisissez le personnage a gerer</p>
              </div>
            </div>
          </div>
          <a href={config.DISCORD_LOGIN_URL} className="btn-discord">
            <i className="fa-brands fa-discord" />
            Se connecter avec Discord
          </a>
        </div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer className="landing-footer">
      <div className="section-inner">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/logo.png" alt="Fleeca Bank" className="footer-logo" onError={e => { e.target.style.display='none'; }} />
            <span className="footer-logo-text">Fleeca Bank</span>
            <p className="footer-tagline">Votre banque sur Nexa RP</p>
          </div>
          <div className="footer-links">
            <span className="footer-link-title">Navigation</span>
            <a href="#services" className="footer-link">Services</a>
            <a href="#securite" className="footer-link">Securite</a>
            <a href="#acces" className="footer-link">Acces</a>
          </div>
          <div className="footer-links">
            <span className="footer-link-title">Acces rapide</span>
            <a href={config.DISCORD_LOGIN_URL} className="footer-link">
              <i className="fa-brands fa-discord" /> Connexion
            </a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Fleeca Bank — Nexa RP. Tous droits reserves.</p>
          <p>Serveur de roleplay fictif. Toute ressemblance avec une banque reelle est purement fortuite.</p>
        </div>
      </div>
    </footer>
  );
}

// ==================== LANDING PAGE ====================
export default function Landing() {
  // Si ouvert dans FiveM → écran d'attente du token NUI
  if (isIngame()) return <NuiAutoLogin />;

  return (
    <div className="landing-page">
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <SecuritySection />
      <AccessSection />
      <Footer />
    </div>
  );
}
